// Media Matchers API integration
// Docs: https://api.media-matchers.com/search

const API_BASE = 'https://api.media-matchers.com/search'
const M_PUB_ID = '36984'
const M_PUB_CAMPAIGN_ID = '27152'

export interface MatchingApiParams {
  degreeInterest?: string
  mProgram?: string
  campusType?: string
  militaryAffiliation?: string
  startTimeframe?: string
  zipCode?: string
  sub1?: string
  sub2?: string
  sub3?: string
}

export interface MatchedSchool {
  // Flexible: accept any field names the API returns
  [key: string]: unknown
}

export interface MatchingApiResponse {
  schools: MatchedSchool[]
  total: number
  success: boolean
  rawResponse?: unknown
}

function findSchoolArray(data: unknown): MatchedSchool[] {
  // If it's already an array, return it
  if (Array.isArray(data)) return data

  // If it's an object, look for any array property
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>

    // Check common keys first
    const commonKeys = [
      'schools',
      'results',
      'data',
      'listings',
      'matches',
      'items',
      'records',
    ]
    for (const key of commonKeys) {
      if (Array.isArray(obj[key])) return obj[key] as MatchedSchool[]
    }

    // Check any key that has an array value
    for (const key of Object.keys(obj)) {
      if (Array.isArray(obj[key]) && (obj[key] as unknown[]).length > 0) {
        return obj[key] as MatchedSchool[]
      }
    }
  }

  return []
}

export async function searchSchools(
  params: MatchingApiParams,
): Promise<MatchingApiResponse> {
  const queryParams = new URLSearchParams()

  // Required fixed params
  queryParams.set('mPubID', M_PUB_ID)
  queryParams.set('mPubCampaignID', M_PUB_CAMPAIGN_ID)
  queryParams.set('mediaType', '1')
  queryParams.set('gradYear', '2015')
  queryParams.set('highestEducation', '1')
  queryParams.set('startTimeframe', '1')

  // Dynamic params - only add if provided
  if (params.degreeInterest)
    queryParams.set('degreeInterest', params.degreeInterest)
  if (params.mProgram) queryParams.set('mProgram', params.mProgram)
  if (params.campusType) queryParams.set('campusType', params.campusType)
  if (params.militaryAffiliation)
    queryParams.set('militaryAffiliation', params.militaryAffiliation)
  if (params.zipCode) queryParams.set('zipCode', params.zipCode)
  if (params.sub1) queryParams.set('sub1', params.sub1)
  if (params.sub2) queryParams.set('sub2', params.sub2)
  if (params.sub3) queryParams.set('sub3', params.sub3)

  const url = `${API_BASE}?${queryParams.toString()}`
  console.log('[MatchingAPI] Request URL:', url)

  try {
    const response = await fetch(url)
    console.log('[MatchingAPI] Response status:', response.status)

    if (!response.ok) {
      const errorText = await response.text()
      console.error('[MatchingAPI] Error response:', errorText)
      return { schools: [], total: 0, success: false }
    }

    const rawText = await response.text()
    console.log(
      '[MatchingAPI] Raw response (first 2000 chars):',
      rawText.substring(0, 2000),
    )

    let data: unknown
    try {
      data = JSON.parse(rawText)
    } catch {
      console.error('[MatchingAPI] Failed to parse JSON')
      return { schools: [], total: 0, success: false }
    }

    console.log('[MatchingAPI] Parsed response:', data)
    console.log('[MatchingAPI] Response type:', typeof data)
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      console.log(
        '[MatchingAPI] Response keys:',
        Object.keys(data as Record<string, unknown>),
      )
    }

    const schools = findSchoolArray(data)
    console.log('[MatchingAPI] Found schools:', schools.length)
    if (schools.length > 0) {
      console.log('[MatchingAPI] First school keys:', Object.keys(schools[0]))
      console.log('[MatchingAPI] First school:', schools[0])
    }

    return {
      schools,
      total: schools.length,
      success: true,
      rawResponse: data,
    }
  } catch (error) {
    console.error('[MatchingAPI] Fetch error:', error)
    return { schools: [], total: 0, success: false }
  }
}
